/**
 * Field - Shared field display component for output sections
 *
 * Uses the schema context to format enum values to human-readable labels.
 * When fieldId is provided, uses path-scoped enum mapping to avoid
 * incorrectly mapping numeric values to enum labels from other fields.
 *
 * Renders Review buttons and extracted value display (sparkle + accept)
 * by navigating per-item metadata and data from ReviewContext.
 */

import { DispositionFieldIcon, SectionScoreBadge } from '@components/ui/disposition-status';
import { HeardSeekButton } from '@components/ui/heard-seek-button';
import { ReasoningTooltip, ReviewButton } from '@components/ui/review-button';
import { Sparkles } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import {
  getFieldEvidence,
  getFieldExtractedValue,
  getFieldHeardSeek,
  useDispositionContext,
  useFieldPath,
  useReviewContext,
  useSchemaContextSafe,
} from '../context';

interface FieldProps {
  label: string;
  value: unknown;
  /** Field ID for path-scoped enum lookup (e.g., "coinsurance", "coverage_status") */
  fieldId?: string;
}

export const Field: React.FC<FieldProps> = ({ label, value, fieldId }) => {
  const { formatValue, formatFieldValue } = useSchemaContextSafe();
  const pathPrefix = useFieldPath();
  const [isHovered, setIsHovered] = useState(false);
  const {
    outputItemMeta,
    outputSourceCallId,
    extractedItemMeta,
    extractedSourceCallId,
    extractedItemData,
    onNavigateToCall,
    isHumanAudit,
    onAcceptExtracted,
    currentCallId,
  } = useReviewContext();
  const { fieldStatusMap } = useDispositionContext();

  // Disposition lookup uses item-relative paths. pathPrefix carries the
  // entity (e.g. "drug.coverage"); we strip the leading entity segment so
  // the key matches what extractItemDispositionFields produced.
  const dispositionKey = useMemo(() => {
    if (!fieldId) return undefined;
    const subPrefix = pathPrefix.includes('.') ? pathPrefix.split('.').slice(1).join('.') : '';
    return subPrefix ? `${subPrefix}.${fieldId}` : fieldId;
  }, [pathPrefix, fieldId]);
  const dispositionStatus = dispositionKey ? fieldStatusMap.get(dispositionKey) : undefined;
  const hasDisposition = fieldStatusMap.size > 0;

  // Use field-aware formatting when fieldId is provided
  const displayValue = fieldId ? formatFieldValue(value, fieldId, pathPrefix) : formatValue(value);

  // Navigate item metadata to find per-field evidence
  const outputEvidence = getFieldEvidence(outputItemMeta, pathPrefix, fieldId);
  const extractedEvidence = getFieldEvidence(extractedItemMeta, pathPrefix, fieldId);

  // Per-field "heard" seek descriptor (set when the field was marked heard during a call)
  const outputHeardSeek = getFieldHeardSeek(outputItemMeta, pathPrefix, fieldId);

  // Navigate extracted data to find the AI-extracted value for this field
  const extractedValue = getFieldExtractedValue(extractedItemData, pathPrefix, fieldId);

  // Format the extracted value for display
  const extractedDisplayValue = useMemo(() => {
    if (extractedValue === undefined || extractedValue === null) return undefined;
    if (fieldId) return formatFieldValue(extractedValue, fieldId, pathPrefix);
    return formatValue(extractedValue);
  }, [extractedValue, fieldId, pathPrefix, formatFieldValue, formatValue]);

  // Show extracted value when it differs from the main value.
  // When currentCallId is set, only show sparkle for extractions from the current call.
  const shouldShowExtracted = useMemo(() => {
    if (!extractedDisplayValue || extractedDisplayValue === '--') return false;
    if (currentCallId && extractedSourceCallId && extractedSourceCallId !== currentCallId) {
      return false;
    }
    return (
      extractedDisplayValue.toLowerCase() !== displayValue.toLowerCase() || displayValue === '--'
    );
  }, [extractedDisplayValue, displayValue, currentCallId, extractedSourceCallId]);

  // Build the field path for accept callback (e.g., "drug.0.coverage.coverage_status")
  const fieldPath = fieldId ? `${pathPrefix}.${fieldId}` : pathPrefix;

  return (
    <div
      className="px-2 py-1.5"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="text-xs text-muted-foreground mb-0.5 flex items-center gap-1.5">
        {hasDisposition && <DispositionFieldIcon status={dispositionStatus} />}
        <span>{label}</span>
      </div>
      <div className="inline-flex items-center gap-1.5">
        <span className="text-sm font-medium">{displayValue}</span>
        {onNavigateToCall && !!outputEvidence?.length && (
          <>
            <ReviewButton
              sourceCallId={outputSourceCallId}
              evidence={outputEvidence}
              onNavigateToCall={onNavigateToCall}
              className="text-xs"
            />
            <ReasoningTooltip evidence={outputEvidence} />
          </>
        )}
      </div>

      {/* Extracted value display — sparkle + value + Review */}
      {shouldShowExtracted && (
        <div className="mt-1.5">
          {isHumanAudit && isHovered && onAcceptExtracted ? (
            <div className="flex items-center gap-2 text-xs">
              <Sparkles className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              <button
                onClick={() => onAcceptExtracted(fieldPath, extractedValue, label)}
                className="text-muted-foreground hover:underline hover:text-foreground"
              >
                Accept {extractedDisplayValue}?
              </button>
              {onNavigateToCall && (
                <>
                  <ReviewButton
                    sourceCallId={extractedSourceCallId}
                    evidence={extractedEvidence}
                    onNavigateToCall={onNavigateToCall}
                  />
                  <ReasoningTooltip evidence={extractedEvidence} />
                </>
              )}
              {onNavigateToCall && outputHeardSeek?.source_id && (
                <HeardSeekButton
                  heardSeek={outputHeardSeek}
                  onNavigateToCall={onNavigateToCall}
                  className="text-xs"
                />
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              <span>{extractedDisplayValue}</span>
              {onNavigateToCall && (
                <>
                  <ReviewButton
                    sourceCallId={extractedSourceCallId}
                    evidence={extractedEvidence}
                    onNavigateToCall={onNavigateToCall}
                    className="text-xs"
                  />
                  <ReasoningTooltip evidence={extractedEvidence} />
                </>
              )}
              {onNavigateToCall && outputHeardSeek?.source_id && (
                <HeardSeekButton
                  heardSeek={outputHeardSeek}
                  onNavigateToCall={onNavigateToCall}
                  className="text-xs"
                />
              )}
            </div>
          )}
        </div>
      )}
      {/* Heard affordance when no extracted row is present to host it inline. */}
      {!shouldShowExtracted && onNavigateToCall && outputHeardSeek?.source_id && (
        <div className="mt-1.5 flex items-center gap-1.5">
          <HeardSeekButton
            heardSeek={outputHeardSeek}
            onNavigateToCall={onNavigateToCall}
            className="text-xs"
          />
        </div>
      )}
    </div>
  );
};

/**
 * Renders the per-section "X/Y captured" badge inside an AccordionTrigger,
 * pulling the score for `subsectionKey` (e.g. "coverage") from DispositionContext.
 * Renders nothing when no score exists for the key.
 */
export const SectionTriggerScore: React.FC<{ subsectionKey: string }> = ({ subsectionKey }) => {
  const { sectionScores } = useDispositionContext();
  return <SectionScoreBadge score={sectionScores.get(subsectionKey)} />;
};
